import _get from 'lodash/get';
import {
  Task,
  TaskFilterInputObject,
  TaskScopeInputObject,
  TaskScopeNameEnum,
  TasksDuplicationListQueryVariables,
  useTasksDuplicationListQuery,
} from 'graphql-common';
import { GetUrlParams } from '@lib/enums/urls';
import createSearchFilters, {
  FilterSchemaPredicateType,
} from '@lib/utils/createSearchFilters';
import { ListQueryHookProps, ListQueryHookResult } from '../types';

type UseTasksQueryHookProps = ListQueryHookProps & {
  scope?: TaskScopeInputObject;
};

const DEFAULT_SEARCH_SCHEMA = [
  { fieldName: 'name' },
  { fieldName: 'formTemplateName' },
  { fieldName: 'formCategoryName' },
  { fieldName: 'siteName' },
  { fieldName: 'siteAreaName' },
  { fieldName: 'siteCode' },
  { fieldName: 'siteAreaCode' },
  { fieldName: 'priority', predicateType: FilterSchemaPredicateType.Eq },
];

export default function useTasksQueryHook({
  additionalFilters,
  fetchPolicy = 'cache-and-network',
  // ids,
  onCompleted,
  onError,
  orderingParams,
  paginationParams,
  scope,
  scopeName,
  searchQuery,
  searchQueryFilterSchema,
  skip,
  usePrevious = true,
}: UseTasksQueryHookProps): ListQueryHookResult<Task> {
  const page = _get(paginationParams, GetUrlParams.Page);
  const perPage = _get(paginationParams, GetUrlParams.PerPage);
  const orderingField = _get(orderingParams, GetUrlParams.OrderingField);
  const orderingDirection = _get(
    orderingParams,
    GetUrlParams.OrderingDirection,
  );

  const resolvedScope: TaskScopeInputObject = scope ?? {
    name: scopeName as TaskScopeNameEnum,
  };

  const pageNumber = Number(page);
  const limitNumber = Number(perPage);
  const variables: TasksDuplicationListQueryVariables = {
    scope: resolvedScope,
    page: Number.isNaN(pageNumber) ? undefined : pageNumber,
    limit: Number.isNaN(limitNumber) ? undefined : limitNumber,
  };

  const filters: TaskFilterInputObject[] = [];

  if (searchQuery) {
    const schema =
      searchQueryFilterSchema && searchQueryFilterSchema.length
        ? searchQueryFilterSchema.map((fieldName) => ({ fieldName }))
        : DEFAULT_SEARCH_SCHEMA;
    filters.push(...createSearchFilters(searchQuery, schema));
  }

  // if (ids?.length) {
  //   filters.push({
  //     id: {
  //       grouping: FilterGroupingEnum.Or,
  //       predicate: {
  //         in: ids,
  //       },
  //     },
  //   });
  // }

  if (additionalFilters?.length) {
    filters.push(...additionalFilters);
  }

  if (filters.length) {
    variables.filters = filters;
  }

  if (orderingField && orderingDirection) {
    variables.sorts = {
      [orderingField]: orderingDirection,
    };
  }

  const { data, previousData, loading, error, refetch } =
    useTasksDuplicationListQuery({
      fetchPolicy,
      variables,
      skip,
      onCompleted,
      onError,
    });

  const queryData = usePrevious ? data || previousData : data;
  const firstLoading = loading && previousData === undefined;
  const collection =
    !usePrevious && loading
      ? []
      : (_get(queryData, 'tasks.collection', []) as Task[]);
  const metadata = _get(queryData, 'tasks.metadata');
  const totalCount = _get(metadata, 'totalCount', 0);
  const totalPages = _get(metadata, 'totalPages', 0);

  return {
    collection,
    error,
    firstLoading,
    loading,
    refetch,
    totalCount,
    totalPages,
  };
}
